/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {CreditItem} from "types/entities/PackagesItems";
import {useBookingPackagesRowInitialization} from "../hooks/useBookingPackagesRowInitialization";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import BookingPackagesCreditsDriverInfo from "./BookingPackagesCreditsDriverInfo";
import BookingPackagesCreditsPriceDisplay from "./BookingPackagesCreditsPriceDisplay";
import {useTypedSelector} from "helpers/reducers";
import Dates from "utils/Dates";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";
import clsx from "clsx";
import BookingPackagesTicketsSelectStandard from "./BookingPackagesTicketsSelectStandard";
import BookingPackagesCreditsSelectStandard from "./BookingPackagesCreditsSelectStandard";
import FeatherIcon from "components/FeatherIcon";
import {currencyNumberFormat} from "utils/utility";
import {closeModal, openModal} from "helpers/actions";
import {CreditsExplainerPage} from "pages/Front/Booking/Credits/CreditCalculator/components/CreditsExplainerPage";
import {ModalSize} from "pages/Front/components/Popup";
import {useDispatch} from "useDispatch";
import OnlineTicket from "components/Icons/OnlineTicket";
import PackageCreditInfoPillow
    from "pages/Front/Booking/components/BookingPackageNew/components/PackageCreditInfoPillow";

interface BookingPackagesDriverRowStandardProps {
    index: number;
    i: number;
    personType: PackageItemPersonType;
}

const BookingPackagesDriverRowStandard = (props: BookingPackagesDriverRowStandardProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const availableCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.availableCreditItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const {setPackageItemHandler, setPackageCreditItemHandler, setPackageCreditItemsHandler, getPackageItemByDuration, getPackageCreditItemById} =
        usePackageItemKartingOptions();
    const {chosenDuration, selectedPackageItem, handleDurationChange} = useBookingPackagesRowInitialization(props.index, props.personType);

    const creditItems = availableCreditItems.filter(item => item?.article?.creditPackage?.category === PackageCreditItemType.Standard);
    const [isExpanded, setIsExpanded] = useState<boolean>(props.i === 0);

    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    const selectedPackageCreditItemByPerson = selectedPackageCreditItems.find(item => item.personIndex === props.index);
    const selectedPackageCreditItem = getPackageCreditItemById(selectedPackageCreditItemByPerson?.id || null);
    const sortedPackageCreditItems = [...creditItems].sort((a, b) => a.article.article_value - b.article.article_value);

    const handlePackageCreditItemChange = (creditItem: CreditItem | null) => {
        setPackageCreditItemHandler(props.index, creditItem?.id || null, props.personType, 1);
    };

    const handleApplyToAll = () => {
        const newCreditItems = bookingProcess.selectedPackageItems.map(item => {
            return {
                personIndex: item.personIndex,
                id: selectedPackageCreditItem?.id || null,
                person_type: item.person_type,
                quantity: 1,
            };
        });

        bookingProcess.selectedPackageItems.forEach(item => {
            if (item.personIndex !== props.index) {
                const packageItem = getPackageItemByDuration(chosenDuration, item.person_type);
                if (packageItem) {
                    setPackageItemHandler(item.personIndex as number, packageItem.id, item.person_type, 1);
                }
            }
        });

        setPackageCreditItemsHandler(newCreditItems);
    };

    const getSummaryText = () => {
        const durationHours = chosenDuration ? chosenDuration / 60 : null;
        const durationLabel = durationHours !== null ? (durationHours === 6 ? t("Day ticket") : durationHours + " " + t("h")) : "---";
        const creditLabel = selectedPackageCreditItem ? selectedPackageCreditItem.article?.creditPackage?.name ?? t("Credits") : t("No credits");
        const spacer = selectedPackageCreditItem ? "+" : "·";
        return `${durationLabel} ${spacer} ${creditLabel}`;
    };

    const getTotalPrice = () => {
        const ticketPrice = selectedPackageItem?.price || 0;
        const creditPrice = selectedPackageCreditItem?.price || 0;
        return ticketPrice + creditPrice > 0 ? ticketPrice + creditPrice : null;
    };

    const openCreditCalculatorModal = () => {
        dispatch(openModal(<CreditsExplainerPage onClose={() => dispatch(closeModal())} />, {modalSize: ModalSize.Scrollable}));
    };
    return (
        <>
            {/* Mobile view (< lg): accordion */}
            <div className={"BookingPackagesDriverRowStandardMobile bg-[#F5F5F566] mb-2 lg:hidden w-full border border-black/5 rounded-xl overflow-hidden"}>
                {/* Accordion header */}
                <button
                    type={"button"}
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={clsx(
                        "w-full flex flex-row items-center justify-between px-2 py-3 gap-3"
                        // isExpanded ? "border-b border-[#E5E7EB]" : ""
                    )}
                >
                    <div className={"flex items-center gap-2 shrink-0"}>
                        <BookingPackagesCreditsDriverInfo i={props.i} personType={props.personType} category={bookingProcess.filter?.category}>
                            {!isExpanded && <span className={"text-xs text-black truncate leading-none"}>{getSummaryText()}</span>}
                        </BookingPackagesCreditsDriverInfo>
                    </div>

                    <div className={"flex items-center gap-2 min-w-0 flex-1 justify-end"}>
                        <span className={"font-bold text-sm text-black shrink-0"}>
                            {getTotalPrice() !== null ? currencyNumberFormat(getTotalPrice()!) : "---"}
                        </span>
                        <FeatherIcon name={isExpanded ? "chevron-up" : "chevron-down"} className={"size-4 flex-none text-black/90"} />
                    </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                    <div className={"px-2 pt-3 pb-2 flex flex-col gap-2 md:gap-4 border-t border-[#E5E7EB] border-dashed"}>
                        {/* Ticket section */}
                        <div>
                            <div className={"flex items-center gap-2 mb-2 flex-wrap"}>
                                <OnlineTicket className={"size-[20px] flex-none"} />
                                <span className={"font-semibold text-sm text-black"}>{t("Ticket")}</span>
                                <span className={"text-[11px] text-black bg-brand-yellow px-2 py-0.5 rounded-full"}>{t("Required")}</span>
                            </div>
                            {props.i === 0 && (
                                <div className={"flex mb-2"}>
                                    <PackageCreditInfoPillow
                                        color={"yellow"}
                                        title={t("Entry includes unlimited use of over 20 standard attractions.")}
                                        subtitle={""}
                                    />
                                </div>
                            )}
                            <BookingPackagesTicketsSelectStandard
                                showLabels={true}
                                disabled1Hour={Dates.hour(selectedTimeslot?.start_at) < 17}
                                disabled2Hours={false}
                                chosenDuration={chosenDuration}
                                className={""}
                                enabled={true}
                                personType={props.personType}
                                setChosenDuration={handleDurationChange}
                                withKarting={0}
                            />
                        </div>

                        {/* Plus separator */}
                        <div className={"flex justify-center"}>
                            <span className={"text-[18px] font-bold text-[#D9D9D9]"}>+</span>
                        </div>

                        {/* Credits section */}
                        <div>
                            <div className={"flex items-center gap-2 mb-2 flex-wrap"}>
                                <img src="/img/credits/arbo-credits-icon.svg" alt="credits icon" className="size-[20px] flex-none" />
                                <span className={"font-semibold text-sm text-black"}>{t("Credits")}</span>
                                <span className={"text-[11px] text-white bg-arbo-red px-2 py-0.5 rounded-full"}>{t("Optional")}</span>
                            </div>
                            {props.i === 0 && (
                                <div className={"flex mb-2"}>
                                    <PackageCreditInfoPillow
                                        color={"red"}
                                        title={t("For the attractions of the Speed & Drift Zone") + ":"}
                                        subtitle={"Go-Karting · Crazy Drift" + " \r\n" + t("Credits are personal and non-transferable")}
                                    />
                                </div>
                            )}
                            <BookingPackagesCreditsSelectStandard
                                showLabels={true}
                                sortedPackageCreditItems={sortedPackageCreditItems}
                                selectedPackageCreditItem={selectedPackageCreditItem}
                                handlePackageCreditItemChange={handlePackageCreditItemChange}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop view (>= lg): grid */}
            <div
                className={clsx(
                    "BookingPackagesDriverRowStandard hidden lg:grid grid-cols-[10fr_37fr_44fr_9fr] xl:grid-cols-[10fr_36fr_42fr_12fr] w-full gap-4 px-2",
                    props.i % 2 == 1 && "bg-[#f9f9f9] py-1"
                )}
            >
                <div className={"w-full items-center justify-center flex flex-col"}>
                    <BookingPackagesCreditsDriverInfo i={props.i} personType={props.personType} category={bookingProcess.filter?.category} />
                </div>
                <div className={"relative w-full items-center flex flex-row"}>
                    <span className={"text-[18px] font-bold text-[#D9D9D9] absolute right-[-13px] -translate-y-1/2 pointer-events-none top-1/2"}>+</span>
                    <BookingPackagesTicketsSelectStandard
                        disabled1Hour={Dates.hour(selectedTimeslot?.start_at) < 17}
                        disabled2Hours={false}
                        chosenDuration={chosenDuration}
                        className={"justify-start lg:justify-center"}
                        enabled={true}
                        personType={props.personType}
                        setChosenDuration={handleDurationChange}
                        withKarting={0}
                    />
                </div>
                <div className={"relative w-full items-center"}>
                    <span className={"text-[18px] font-bold text-[#D9D9D9] absolute right-[-13px] -translate-y-1/2 pointer-events-none top-1/2"}>=</span>
                    <BookingPackagesCreditsSelectStandard
                        sortedPackageCreditItems={sortedPackageCreditItems}
                        selectedPackageCreditItem={selectedPackageCreditItem}
                        handlePackageCreditItemChange={handlePackageCreditItemChange}
                    />
                </div>
                <div className={"w-full items-end xl:items-center justify-center flex flex-col"}>
                    <BookingPackagesCreditsPriceDisplay
                        category={bookingProcess.filter?.category}
                        personType={props.personType}
                        i={props.i}
                        selectedPackageItem={selectedPackageItem}
                        selectedPackageCreditItem={selectedPackageCreditItem}
                    />
                </div>
            </div>
        </>
    );
};

export default BookingPackagesDriverRowStandard;
